import { authFetch } from '@/services/api.service';

export interface CashMovementCategory {
  id: number;
  code: string;
  name: string;
  description?: string;
  type: 'income' | 'expense';
  requiresApproval: boolean;
  isActive: boolean;
  orderIndex: number;
  icon?: string;
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CashMovementCategoriesResponse {
  success: boolean;
  data: {
    income: CashMovementCategory[];
    expense: CashMovementCategory[];
    all: CashMovementCategory[];
  };
  summary: {
    total: number;
    income: number;
    expense: number;
  };
}

export interface CashMovementCategoriesFilteredResponse {
  success: boolean;
  data: CashMovementCategory[];
  total: number;
}

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

class CashMovementCategoriesService {
  async getAllCategories(): Promise<CashMovementCategoriesResponse> {
    try {
      const response = await authFetch(`${BASE_URL}/cash-movement-categories`);

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching cash movement categories:', error);
      throw error;
    }
  }

  async getCategoriesByType(type: 'income' | 'expense'): Promise<CashMovementCategoriesFilteredResponse> {
    try {
      const response = await authFetch(`${BASE_URL}/test-cash-categories?type=${type}&active=true`);

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error fetching ${type} categories:`, error);
      throw error;
    }
  }

  async getCategoryById(id: number): Promise<{ success: boolean; data: CashMovementCategory }> {
    try {
      const response = await authFetch(`${BASE_URL}/cash-movement-categories/${id}`);

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error fetching category ${id}:`, error);
      throw error;
    }
  }

  async createCategory(categoryData: Omit<CashMovementCategory, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>): Promise<{ success: boolean; data: CashMovementCategory }> {
    try {
      const response = await authFetch(`${BASE_URL}/cash-movement-categories`, {
        method: 'POST',
        body: JSON.stringify(categoryData),
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }

  async updateCategory(id: number, categoryData: Partial<CashMovementCategory>): Promise<{ success: boolean; data: CashMovementCategory }> {
    try {
      const response = await authFetch(`${BASE_URL}/cash-movement-categories/${id}`, {
        method: 'PUT',
        body: JSON.stringify(categoryData),
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error updating category ${id}:`, error);
      throw error;
    }
  }

  async toggleCategory(id: number): Promise<{ success: boolean; data: CashMovementCategory }> {
    try {
      const response = await authFetch(`${BASE_URL}/cash-movement-categories/${id}/toggle`, {
        method: 'PATCH',
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error toggling category ${id}:`, error);
      throw error;
    }
  }

  async deleteCategory(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await authFetch(`${BASE_URL}/cash-movement-categories/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error deleting category ${id}:`, error);
      throw error;
    }
  }
}

export const cashMovementCategoriesService = new CashMovementCategoriesService();